/**
 * How a man goes down and what he does once he is there (05 §6.2): two deaths that both end
 * in the corpse pose, the wounded man, the prisoner on his knees, and the ladder climb.
 * The falls are keyed (poseBlend.ts) and read on the 12 Hz pose grid.
 */

import { type BoneOf, carry, dead, onTwos, type Rig, resetPose, steppedPhase } from './poseBase';
import { handsUp } from './poseActions';
import { type Keys, playKeys } from './poseBlend';
import { B } from './rig';

/** Shot: reels, knees go, topples onto his back. */
export const CRUMPLE_SECONDS = 0.75;
/** Blast: lifted off his feet and thrown onto his back. */
export const BLAST_SECONDS = 0.8;
/** A wounded man takes this long to get from his feet to the ground. */
export const WOUNDED_FALL_SECONDS = 0.5;

/* --------------------------------------------------------------------------- crumple */

/** The instant of the hit: chest back, chin up, grip gone. */
const reel = (bone: BoneOf): void => {
  bone(B.pelvis).position.y = 0.84;
  bone(B.spine).rotation.set(-0.3, 0, 0.1);
  bone(B.head).rotation.x = -0.35;
  bone(B.lUpperArm).rotation.set(-0.3, 0, 0.4);
  bone(B.lForeArm).rotation.x = -0.6;
  bone(B.rUpperArm).rotation.set(-0.2, 0, -0.5);
  bone(B.rForeArm).rotation.x = -0.6;
  bone(B.weapon).position.set(-0.3, 0.05, 0.25);
  bone(B.weapon).rotation.set(0.6, 0.5, 0);
};

/** Knees on the ground, slumped over them, the rifle already fallen. */
const buckle = (bone: BoneOf): void => {
  bone(B.pelvis).position.set(0, 0.46, -0.05);
  for (const thigh of [B.lThigh, B.rThigh]) bone(thigh).rotation.x = -1.15;
  for (const shin of [B.lShin, B.rShin]) bone(shin).rotation.x = 2.1;
  bone(B.spine).rotation.set(0.55, 0, 0.12);
  bone(B.head).rotation.x = 0.5;
  bone(B.lUpperArm).rotation.set(-0.3, 0, 0.15);
  bone(B.rUpperArm).rotation.set(-0.3, 0, -0.15);
  bone(B.weapon).position.set(-0.45, -0.35, 0.2);
  bone(B.weapon).rotation.set(1.2, 0.7, 0);
};

/** Going over backwards: most of the way to the corpse, legs not yet settled. */
const topple = (bone: BoneOf): void => {
  dead(bone);
  bone(B.pelvis).position.set(0, 0.24, 0);
  bone(B.pelvis).rotation.set(-1.1, 0, 0.15);
  bone(B.lThigh).rotation.set(-0.4, 0, -0.2);
  bone(B.lShin).rotation.x = 1.0;
  bone(B.rThigh).rotation.set(-0.9, 0, 0.15);
  bone(B.rShin).rotation.x = 1.1;
  bone(B.spine).rotation.x = 0.1;
};

const CRUMPLE_KEYS: Keys = [[0, reel], [0.4, buckle], [0.75, topple], [1, dead]];

/* ----------------------------------------------------------------------------- blast */

const spread = (bone: BoneOf, arms: number): void => {
  bone(B.lUpperArm).rotation.set(0, 0, arms);
  bone(B.rUpperArm).rotation.set(0, 0, -arms);
  bone(B.lForeArm).rotation.x = -0.3;
  bone(B.rForeArm).rotation.x = -0.3;
};

const lifted = (bone: BoneOf): void => {
  bone(B.pelvis).position.y = 0.95;
  bone(B.spine).rotation.x = -0.5;
  bone(B.head).rotation.x = -0.4;
  spread(bone, 2.0);
  bone(B.weapon).position.set(-0.4, 0.3, 0.3);
  bone(B.weapon).rotation.set(0.5, 0.8, 0.2);
};

/** Off the ground, going over backwards, arms and legs flung wide: the silhouette that says "blast". */
const airborne = (bone: BoneOf): void => {
  bone(B.pelvis).position.set(0, 1.35, -0.05);
  bone(B.pelvis).rotation.x = -0.8;
  bone(B.lThigh).rotation.set(-0.9, 0, 0.3);
  bone(B.rThigh).rotation.set(-0.6, 0, -0.3);
  bone(B.lShin).rotation.x = 0.4;
  bone(B.rShin).rotation.x = 0.7;
  bone(B.spine).rotation.x = -0.25;
  bone(B.head).rotation.x = -0.3;
  spread(bone, 2.4);
  bone(B.weapon).position.set(-0.7, 0.4, 0.3);
  bone(B.weapon).rotation.set(1.0, 1.4, 0.5);
};

const falling = (bone: BoneOf): void => {
  airborne(bone);
  bone(B.pelvis).position.set(0, 0.7, 0);
  bone(B.pelvis).rotation.x = -1.35;
  spread(bone, 1.8);
};

/** He lands flat and his legs kick up once before they settle. */
const impact = (bone: BoneOf): void => {
  dead(bone);
  bone(B.pelvis).position.y = 0.12;
  bone(B.lThigh).rotation.set(-0.7, 0, -0.3);
  bone(B.rThigh).rotation.set(-0.9, 0, 0.2);
  bone(B.lShin).rotation.x = 0.2;
  bone(B.head).rotation.x = -0.3;
};

const BLAST_KEYS: Keys = [[0, lifted], [0.3, airborne], [0.62, falling], [0.82, impact], [1, dead]];

export function deathPose(rig: Rig, blast: boolean, age: number): void {
  const seconds = blast ? BLAST_SECONDS : CRUMPLE_SECONDS;
  playKeys(rig, blast ? BLAST_KEYS : CRUMPLE_KEYS, steppedPhase(age, seconds));
}

/* --------------------------------------------------------------------------- wounded */

/** Down on his back and one elbow, a hand to the wound, the near knee drawn up and working. */
function writhe(bone: BoneOf, t: number, vigour: number): void {
  const w = Math.sin(t * 2.4) * vigour;
  const w2 = Math.sin(t * 1.7 + 1) * vigour;
  bone(B.pelvis).position.set(0, 0.2, 0);
  bone(B.pelvis).rotation.set(-1.05, 0, 0.35);
  bone(B.spine).rotation.set(0.75, 0, 0.12 * w);
  bone(B.head).rotation.set(0.35 + 0.1 * w2, 0.3, 0);
  bone(B.lUpperArm).rotation.set(-0.5, 0, -0.7);
  bone(B.lForeArm).rotation.x = -1.7;
  bone(B.rUpperArm).rotation.set(0.5, 0, -0.5);
  bone(B.rForeArm).rotation.x = -0.3;
  bone(B.lThigh).rotation.x = -1.2 + 0.25 * w;
  bone(B.lShin).rotation.x = 1.6 - 0.2 * w;
  bone(B.rThigh).rotation.x = -0.35;
  bone(B.rShin).rotation.x = 0.4 + 0.15 * w2;
  bone(B.weapon).position.set(-0.6, 0.1, -0.25);
  bone(B.weapon).rotation.set(Math.PI / 2, 0.7, 0);
}

/** `age` counts from the hit that put him down; past the fall it is just the writhe (no blending cost). */
export function woundedPose(rig: Rig, age: number, t: number, vigour: number): void {
  if (age >= WOUNDED_FALL_SECONDS) {
    resetPose(rig.bones);
    writhe(rig.bone, t, vigour);
    return;
  }
  const settled = (bone: BoneOf): void => writhe(bone, t, vigour);
  playKeys(rig, [[0, reel], [0.5, buckle], [1, settled]], steppedPhase(age, WOUNDED_FALL_SECONDS));
}

/* ------------------------------------------------------------------ surrender, board */

/** On both knees, hands up, head bowed, the rifle on the ground in front of him. */
export function surrenderPose(bone: BoneOf): void {
  bone(B.pelvis).position.set(0, 0.52, 0);
  for (const thigh of [B.lThigh, B.rThigh]) bone(thigh).rotation.x = 0.1;
  for (const shin of [B.lShin, B.rShin]) bone(shin).rotation.x = 1.5;
  bone(B.spine).rotation.x = 0.08;
  bone(B.head).rotation.x = 0.2;
  handsUp(bone);
  bone(B.weapon).position.set(0.1, -0.58, 0.6);
  bone(B.weapon).rotation.set(0, 1.3, 0);
}

/** Rungs per second up the rope ladder. */
const CLIMB_HZ = 1.6;

/** Hand over hand, knees working, rifle slung across the pack. Whoever places him lifts him. */
export function boardPose(bone: BoneOf, age: number): void {
  const s = Math.sin(onTwos(age) * CLIMB_HZ * Math.PI * 2);
  carry(bone);
  bone(B.spine).rotation.x = 0.12;
  bone(B.head).rotation.x = -0.35;
  bone(B.lUpperArm).rotation.set(-2.5 + 0.5 * s, 0, 0.1);
  bone(B.lForeArm).rotation.set(-0.5 - 0.4 * s, 0, 0);
  bone(B.rUpperArm).rotation.set(-2.5 - 0.5 * s, 0, -0.1);
  bone(B.rForeArm).rotation.set(-0.5 + 0.4 * s, 0, 0);
  bone(B.lThigh).rotation.x = -0.9 * Math.max(0, -s) - 0.1;
  bone(B.lShin).rotation.x = 1.3 * Math.max(0, -s) + 0.1;
  bone(B.rThigh).rotation.x = -0.9 * Math.max(0, s) - 0.1;
  bone(B.rShin).rotation.x = 1.3 * Math.max(0, s) + 0.1;
  bone(B.weapon).position.set(0.05, 0.3, -0.4);
  bone(B.weapon).rotation.set(-Math.PI / 2 + 0.2, 0.5, 0);
}
